import React from 'react';

import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {LEAF_TYPE_TO_ICON} from 'data-types/benchling-type-def/leafTypeToIcon';
import {
  CommonPlateField,
  getCommonPlateFieldDisplayLabel,
} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

// A common plate field is identified by (name, type) — the same name can appear more than once
// across a batch of plates with different types (see getUnionPlateFields). The dropdown works
// with a single string value per option, so we encode the pair into one opaque token and decode
// it on selection, mirroring ScratchPlateSourceColumnSelector's layer-qualified encoding.
const TOKEN_SEPARATOR = '';

export function encodePlateFieldValue(name: string, type: LeafType): string {
  return `${name}${TOKEN_SEPARATOR}${type}`;
}

export function decodePlateFieldValue(token: string): {name: string; type: LeafType} {
  const [name, type, ...rest] = token.split(TOKEN_SEPARATOR);
  if (type === undefined || rest.length > 0) {
    throw new Error(`Malformed plate field token; expected name${TOKEN_SEPARATOR}type.`);
  }
  return {name, type: type as LeafType};
}

function fieldIcon(field: CommonPlateField): BenchlingIconName {
  return LEAF_TYPE_TO_ICON[field.type];
}

export interface ScratchPlatePlateFieldSelectorProps {
  /** Common plate fields, ungrouped — unlike well columns, plate fields aren't scoped to a layer. */
  fields: Array<CommonPlateField>;
  /** The currently selected field, encoded via `encodePlateFieldValue`. */
  selectedValue: string | null;
  onChange: (value: string | null) => void;
  allowSelectNull: boolean;
  disabled?: boolean;
}

/**
 * Dropdown that selects one common plate field. This is a flat sibling of
 * `ScratchPlateSourceColumnSelector` — plate fields aren't grouped into layers, so this component
 * skips the header rows, but reuses the same underlying pieces (`SelectDropdown` over a
 * `SearchableMenu`, with a type-icon `Chip`) so the two selectors look and behave identically.
 *
 * A field name can collide across different types (see getUnionPlateFields), so options are
 * encoded with both name and type to keep them distinct, and labeled via
 * getCommonPlateFieldDisplayLabel to disambiguate a collision for the user.
 */
export default function ScratchPlatePlateFieldSelector({
  fields,
  selectedValue,
  onChange,
  allowSelectNull,
  disabled,
}: ScratchPlatePlateFieldSelectorProps): JSX.Element {
  const fieldsByValue = new Map<string, CommonPlateField>();
  for (const field of fields) {
    fieldsByValue.set(encodePlateFieldValue(field.name, field.type), field);
  }
  const selectedField = selectedValue != null ? fieldsByValue.get(selectedValue) : undefined;

  const optionDatas: Array<OptionData> = [
    ...(allowSelectNull ? [{name: 'None', value: null}] : []),
    ...fields.map((field) => ({
      icon: fieldIcon(field),
      name: getCommonPlateFieldDisplayLabel(field, fields),
      value: encodePlateFieldValue(field.name, field.type),
    })),
  ];

  const menu = (
    <SearchableMenu
      height={MenuHeight.MEDIUM}
      width={MenuWidth.FULL}
      selectedValue={selectedValue}
      onSelect={(value): void => {
        onChange(value);
      }}
      optionDatas={optionDatas}
    />
  );

  return (
    <SelectDropdown
      menu={menu}
      disabled={disabled}
      displayName={
        selectedField != null ? (
          <Chip
            size={ChipSize.SMALL}
            icon={fieldIcon(selectedField)}
            text={getCommonPlateFieldDisplayLabel(selectedField, fields)}
            isReadonly={true}
          />
        ) : (
          'None'
        )
      }
    />
  );
}
